// src/oura/newscan/flowSteps.ts
//
// The New scan flow's steps and the pure rules the frame runs on: which steps
// a kind of draft has, which one to open at, the foot a step declares, the
// domain normaliser (from domain.ts), and the Business form seeded from a
// website read.
//
// No React here, so the page, the context and the steps share one copy.

import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { ScanDraft, ScanDraftBusiness, ScanDraftDetected, ScanDraftKind, ScanDraftPurpose } from "@service/aeo/scanDraftTypes";
import { STEP_LABEL } from "./copy";

export type StepKey = "website" | "business" | "review" | "start";

const STEPS_BY_KIND: Record<ScanDraftKind, StepKey[]> = {
    new_scan: ["website", "business", "review", "start"],
    edit_set: ["business", "review", "start"],
    recipe_change: ["business", "review", "start"],
};

/** A signed client's First AI check: its site and business are known, so it opens on the drafted 50. */
const FIRST_CHECK_STEPS: StepKey[] = ["business", "review", "start"];

/** The steps a kind of draft walks through, in order. A first_check draft has no Website step. */
export function stepsFor(kind: ScanDraftKind, purpose?: ScanDraftPurpose | null): StepKey[] {
    return purpose === "first_check" ? FIRST_CHECK_STEPS : STEPS_BY_KIND[kind];
}

export function stepLabel(step: StepKey): string {
    return STEP_LABEL[step];
}

export function isStepKey(value: string | null | undefined): value is StepKey {
    return value === "website" || value === "business" || value === "review" || value === "start";
}

/** What the frame knows when it decides which steps can be opened. */
export interface StepReach {
    kind: ScanDraftKind;
    /** `first_check`: a signed client's First AI check (no Website step). */
    purpose?: ScanDraftPurpose | null;
    /** A draft id is in the URL. */
    hasDraftId: boolean;
    draft: ScanDraft | null;
    /** new_scan before a draft: the site was read or a form was typed. */
    hasForm: boolean;
    /** First AI check: staff pressed "Looks right" on Business for this draft (businessSeen.ts). */
    businessSeen?: boolean;
}

/**
 * A draft that can no longer be worked on: discarded (Keep all, Start over,
 * another tab) or superseded. The server answers every write on it with 409.
 */
export function isClosedDraft(draft: ScanDraft | null): boolean {
    return draft !== null && (draft.status === "discarded" || draft.status === "superseded");
}

/** Whether a step can be opened now. Unreachable steps are greyed in the track. */
export function canReachStep(step: StepKey, r: StepReach): boolean {
    if (!stepsFor(r.kind, r.purpose).includes(step)) return false;
    const firstCheck = r.purpose === "first_check";
    // A closed draft opens no step: the page shows the closed state instead.
    if (isClosedDraft(r.draft)) return false;
    switch (step) {
        case "website":
            // The site is fixed once a draft exists: a new site is a new draft.
            return r.kind === "new_scan" && !r.hasDraftId;
        case "business":
            return r.kind === "new_scan" && !firstCheck ? r.hasForm || r.draft !== null : r.draft !== null;
        case "review":
            return r.draft !== null;
        case "start":
            return r.draft !== null && (r.draft.status === "approved" || r.draft.status === "started");
        default:
            return false;
    }
}

/** The step to open when the URL names none, or names one that cannot be reached. */
export function defaultStep(r: StepReach): StepKey {
    if (r.draft) {
        if (r.draft.status === "approved" || r.draft.status === "started") return "start";
        // A First AI check always opens on Business until it is approved, also while the 50 are drafted
        // (Business reads fine meanwhile): Review is the next step, never the landing (Jovi, 20 Oct).
        if (r.purpose === "first_check") return "business";
        return "review";
    }
    if (r.kind === "new_scan" && r.purpose !== "first_check") return r.hasDraftId ? "review" : "website";
    return "review";
}

/* ------------------------------------------------------------------------- *
 * The foot a step declares (rendered by ScanFlowFoot)
 * ------------------------------------------------------------------------- */

export interface FootAction {
    /** Verb first, names what happens: "Draft the 50", "Approve the 50". */
    label: string;
    onClick(): void;
    disabled?: boolean;
    /** Swaps the icon for a spinner and disables the pill. */
    busy?: boolean;
    icon?: LucideIcon;
}

/** The paid-start pattern: the row becomes a sentence, the verb and Cancel. Esc cancels. */
export interface FootConfirm {
    text: ReactNode;
    /** "Start". */
    verb: string;
    busy?: boolean;
    onConfirm(): void;
    onCancel(): void;
}

/**
 * Everything a step puts in the sticky foot. At most: Back on the left, a
 * status line, one secondary and one primary pill on the right.
 */
export interface FootSpec {
    /** The Back pill. `{ onClick }` alone reads "Back". */
    back?: (Omit<FootAction, "label"> & { label?: string }) | null;
    /** Left-hand note when there is no Back ("About 8 seconds. Free."). */
    note?: ReactNode;
    /** 13px ink3 line before the pills ("2 decisions left"). */
    status?: ReactNode;
    /** A quieter second action ("Save draft"). */
    secondary?: FootAction | null;
    /** The one forward action. Blue. */
    primary?: FootAction | null;
    /** Replaces the pills with a confirm row while set. */
    confirm?: FootConfirm | null;
}

/* ------------------------------------------------------------------------- *
 * The site address: one copy, in domain.ts (re-exported for the steps)
 * ------------------------------------------------------------------------- */

export { droppedPath, hostOf, isPathAddressedHost, normalizeDomain } from "./domain";

/* ------------------------------------------------------------------------- *
 * The Business form, seeded from the read
 * ------------------------------------------------------------------------- */

/** The Business step's starting values from a website read. Staff check every one. */
export function formFromDetected(d: ScanDraftDetected): ScanDraftBusiness {
    const buy = d.buyGuess ?? "visit";
    return {
        businessName: d.businessName,
        category: d.category,
        buy,
        market: buy === "online" ? d.market ?? "Malaysia" : d.market,
        city: d.city,
        outlets: buy === "online" ? [] : d.outlets.map((o) => ({ name: o.name, town: o.town || d.city, source: "site" as const })),
        services: d.services.map((name) => ({ name, source: "site" as const })),
        languages: { ms: "auto", zh: "auto" },
        rivals: [],
        otherNames: [],
        requestedBy: null,
    };
}
